import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaShoppingCart, FaPlus, FaTrash } from 'react-icons/fa';
import * as clientService from '../../services/client.service';
import * as clientCatalogService from '../../services/clientCatalog.service';
import * as companyService from '../../services/company.service';
import * as orderService from '../../services/order.service';
import { computeBoxCbmFromDims } from '../../components/TearSheet/tearSheetUtils';
import PageHeader from '../../components/PageHeader/PageHeader';
import './OrderCreation.css';

const num = (v) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const todayStr = () => new Date().toISOString().slice(0, 10);

/** First pack row's quantity-per-pack (used for Inner / Master qty). */
const firstPackQty = (packs) => {
  if (!Array.isArray(packs) || packs.length === 0) return '';
  const v = packs[0]?.quantityPack ?? packs[0]?.quantity_pack;
  return v ?? '';
};

/** Comma-joined color names from a catalog line's color array. */
const colorSummary = (colors) => {
  if (!Array.isArray(colors) || colors.length === 0) return '';
  return colors.map((c) => (typeof c === 'string' ? c : c?.name)).filter(Boolean).join(', ');
};

/** Dimension string, e.g. "27.00x28.00x24.00 CM". */
const formatDimension = (length, breadth, height, unit) => {
  const vals = [length, breadth, height].map((v) => {
    const n = parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n.toFixed(2) : null;
  });
  if (vals.some((v) => v === null)) return '';
  return `${vals[0]}x${vals[1]}x${vals[2]} ${String(unit || '').toUpperCase()}`.trim();
};

/** Tear sheet CBM per carton = sum of each master pack's box CBM. */
const cbmPerCarton = (masterPacks) =>
  (Array.isArray(masterPacks) ? masterPacks : []).reduce(
    (sum, row) => sum + (computeBoxCbmFromDims(row.length, row.breadth, row.height) ?? 0),
    0,
  );

const MODE_OPTIONS = ['BY SEA', 'BY AIR', 'BY ROAD', 'BY RAIL'];
const SHIPMENT_TERMS = ['FOB', 'CIF', 'CFR', 'EXW', 'DAP', 'DDP'];

const OrderCreation = () => {
  const { id: orderId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(orderId);
  // Guards the buyer-change effect from wiping the item list while hydrating an order for edit.
  const hydratingRef = useRef(false);

  const [branches, setBranches] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');

  // Buyer details
  const [buyerPo, setBuyerPo] = useState('');
  const [buyerPoDate, setBuyerPoDate] = useState('');

  // Order information (auto-filled + editable)
  const [companyBranchId, setCompanyBranchId] = useState('');
  const [shippingDate, setShippingDate] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [orderComment, setOrderComment] = useState('');

  // Order summary
  const [recDate, setRecDate] = useState(todayStr());
  const [exFactoryDate, setExFactoryDate] = useState('');
  const [cancelDate, setCancelDate] = useState('');
  const [shipmentTerms, setShipmentTerms] = useState('FOB');
  const [modeOfShipment, setModeOfShipment] = useState('BY SEA');
  const [advanceReceived, setAdvanceReceived] = useState('0');
  const [discountAmount, setDiscountAmount] = useState('0');
  const [addAmount, setAddAmount] = useState('0');
  const [specialNotes, setSpecialNotes] = useState('');

  // Products
  const [catalogRows, setCatalogRows] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [selectedUids, setSelectedUids] = useState(() => new Set());
  const [orderItems, setOrderItems] = useState([]);

  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load buyers + exporter branches once.
  useEffect(() => {
    const load = async () => {
      try {
        const [company, clientRes] = await Promise.all([
          companyService.getCompanyDetails(),
          clientService.getClientList(1, 1000),
        ]);
        const brs = Array.isArray(company?.branches) ? company.branches : [];
        setBranches(brs);
        setClients(Array.isArray(clientRes?.data) ? clientRes.data : []);
        if (brs.length > 0) setCompanyBranchId(String(brs[0].id));
      } catch (e) {
        console.error('Failed to load order prerequisites', e);
      }
    };
    load();
  }, []);

  const selectedClient = useMemo(
    () => clients.find((c) => String(c.clientId || c.client_id) === String(clientId)) || null,
    [clients, clientId],
  );

  // Buyer-derived, read-only context.
  const buyerContext = useMemo(() => {
    const c = selectedClient;
    if (!c) return { currency: '', currencyName: '', paymentTerm: '', portOfDelivery: '', buyerAddress: '', consignee: '' };
    const consigneeName = c.consigneeName || c.consignee_name || '';
    const consigneeAddress = c.consigneeAddress || c.consignee_address || '';
    return {
      currency: c.currency?.symbol || c.currency?.name || '',
      currencyName: c.currency?.name || '',
      paymentTerm: c.paymentTerm?.name || '',
      portOfDelivery: c.portOfDelivery || c.port_of_delivery || '',
      buyerAddress: c.contactAddress || c.contact_address || '',
      consignee: [consigneeName, consigneeAddress].filter(Boolean).join(' — '),
    };
  }, [selectedClient]);

  // Load the buyer's APPROVED catalog products.
  const loadApprovedItems = useCallback(async (code) => {
    if (!code) {
      setCatalogRows([]);
      return;
    }
    setLoadingItems(true);
    setError(null);
    try {
      const catalog = await clientCatalogService.getClientCatalog(code);
      const approved = (catalog || [])
        .filter((item) => (item?.tearSheetStatus || '').toLowerCase() === 'approved')
        .map((item) => {
          const masterPacks = Array.isArray(item.masterPacks) ? item.masterPacks : [];
          return {
            productUid: item.uid,
            pCode: item.pCode || '—',
            image: item.images?.[0]?.path || '',
            description: item.productName || item.itemNo || '—',
            color: colorSummary(item.color),
            dimension: formatDimension(item.length, item.breadth, item.height, item.dimensionUnit),
            clientSku: item.skuId || '—',
            barcode: item.barcodeType?.name || '',
            innerQty: firstPackQty(item.innerPacks),
            masterQty: firstPackQty(item.masterPacks),
            cbmPerCarton: cbmPerCarton(masterPacks),
            price: num(item.tearSheetCost ?? item.cost ?? 0),
          };
        });
      setCatalogRows(approved);
      setSelectedUids(new Set());
    } catch (e) {
      console.error('Failed to load approved items', e);
      setError('Failed to load approved products for this buyer.');
      setCatalogRows([]);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  // Reset the item list when the buyer changes — except while hydrating an order for edit.
  useEffect(() => {
    if (hydratingRef.current) {
      hydratingRef.current = false;
    } else {
      setOrderItems([]);
    }
    loadApprovedItems(clientId);
  }, [clientId, loadApprovedItems]);

  // Edit mode: load the existing order and prefill the form.
  useEffect(() => {
    if (!isEdit) return undefined;
    let active = true;
    (async () => {
      try {
        const order = await orderService.getOrder(orderId);
        if (!active || !order) return;
        hydratingRef.current = true;
        setClientId(order.clientId || '');
        setBuyerPo(order.buyerPo || '');
        setBuyerPoDate(order.buyerPoDate || '');
        setCompanyBranchId(order.companyBranchId ? String(order.companyBranchId) : '');
        setShippingDate(order.shippingDate || '');
        setDeliveryDate(order.deliveryDate || '');
        setOrderComment(order.orderComment || '');
        setRecDate(order.recDate || '');
        setExFactoryDate(order.exFactoryDate || '');
        setCancelDate(order.cancelDate || '');
        setShipmentTerms(order.shipmentTerms || 'FOB');
        setModeOfShipment(order.modeOfShipment || 'BY SEA');
        setAdvanceReceived(String(order.advanceReceivedAmount ?? 0));
        setDiscountAmount(String(order.discountAmount ?? 0));
        setAddAmount(String(order.addAmount ?? 0));
        setSpecialNotes(order.specialNotes || '');
        setOrderItems(
          (order.items || []).map((it) => ({
            ...it,
            cartons: it.cartons ? String(it.cartons) : '',
          })),
        );
      } catch (e) {
        console.error('Failed to load order for editing', e);
        setError('Failed to load order for editing.');
      }
    })();
    return () => {
      active = false;
    };
  }, [isEdit, orderId]);

  const toggleSelect = (uid) => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  };

  // Approved rows filtered by the product search box (P.Code, description, SKU, barcode).
  const filteredCatalogRows = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return catalogRows;
    return catalogRows.filter((r) =>
      [r.pCode, r.description, r.color, r.clientSku, r.barcode]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [catalogRows, productSearch]);

  const allSelected =
    filteredCatalogRows.length > 0 && filteredCatalogRows.every((r) => selectedUids.has(r.productUid));
  const toggleSelectAll = () => {
    setSelectedUids((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredCatalogRows.forEach((r) => next.delete(r.productUid));
      else filteredCatalogRows.forEach((r) => next.add(r.productUid));
      return next;
    });
  };

  // Add the checked catalog rows to the order list (skipping ones already added).
  const addSelectedToList = () => {
    if (selectedUids.size === 0) return;
    setOrderItems((prev) => {
      const existing = new Set(prev.map((r) => r.productUid));
      const additions = catalogRows
        .filter((r) => selectedUids.has(r.productUid) && !existing.has(r.productUid))
        .map((r) => ({ ...r, cartons: '' }));
      return [...prev, ...additions];
    });
    setSelectedUids(new Set());
  };

  const removeFromList = (uid) => {
    setOrderItems((prev) => prev.filter((r) => r.productUid !== uid));
  };

  const handleCartonsChange = (uid, value) => {
    setOrderItems((prev) => prev.map((r) => (r.productUid === uid ? { ...r, cartons: value } : r)));
  };

  // Derived per-line figures + summary.
  const computedItems = useMemo(
    () =>
      orderItems.map((r) => {
        const cartons = num(r.cartons);
        const masterQty = num(r.masterQty);
        const orderQty = cartons * masterQty;
        const totalCbm = num(r.cbmPerCarton) * cartons;
        const netAmount = num(r.price) * orderQty;
        return { ...r, _cartons: cartons, orderQty, totalCbm, netAmount };
      }),
    [orderItems],
  );

  const totalAmount = useMemo(
    () => computedItems.reduce((sum, r) => sum + r.netAmount, 0),
    [computedItems],
  );
  const grandTotal = totalAmount - num(discountAmount) + num(addAmount);

  const canSave = clientId && orderItems.length > 0 && !saving;

  // Clear every field back to its initial state (used after a successful create).
  const resetForm = () => {
    setClientId(''); // clearing the buyer also clears catalog rows + order items via the buyer effect
    setBuyerPo('');
    setBuyerPoDate('');
    setCompanyBranchId(branches.length > 0 ? String(branches[0].id) : '');
    setShippingDate('');
    setDeliveryDate('');
    setOrderComment('');
    setRecDate(todayStr());
    setExFactoryDate('');
    setCancelDate('');
    setShipmentTerms('FOB');
    setModeOfShipment('BY SEA');
    setAdvanceReceived('0');
    setDiscountAmount('0');
    setAddAmount('0');
    setSpecialNotes('');
    setProductSearch('');
    setSelectedUids(new Set());
    setOrderItems([]);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {
        client_id: clientId,
        company_branch_id: companyBranchId ? Number(companyBranchId) : null,
        buyer_po: buyerPo || null,
        buyer_po_date: buyerPoDate || null,
        currency: buyerContext.currencyName || null,
        payment_term: buyerContext.paymentTerm || null,
        port_of_delivery: buyerContext.portOfDelivery || null,
        shipment_terms: shipmentTerms || null,
        mode_of_shipment: modeOfShipment || null,
        buyer_address: buyerContext.buyerAddress || null,
        consignee: buyerContext.consignee || null,
        rec_date: recDate || null,
        ex_factory_date: exFactoryDate || null,
        shipping_date: shippingDate || null,
        delivery_date: deliveryDate || null,
        cancel_date: cancelDate || null,
        advance_received_amount: num(advanceReceived),
        discount_amount: num(discountAmount),
        add_amount: num(addAmount),
        order_comment: orderComment || null,
        special_notes: specialNotes || null,
        items: computedItems.map((r) => ({
          productUid: r.productUid,
          pCode: r.pCode,
          description: r.description,
          color: r.color,
          dimension: r.dimension,
          clientSku: r.clientSku,
          barcode: r.barcode,
          innerQty: r.innerQty,
          masterQty: r.masterQty,
          cbmPerCarton: num(r.cbmPerCarton),
          cartons: r._cartons,
          orderQty: r.orderQty,
          price: num(r.price),
        })),
      };
      if (isEdit) {
        const result = await orderService.updateOrder(orderId, payload);
        setSuccess(`Order ${result?.orderNo || ''} updated successfully. It now needs re-authorization.`);
        navigate('/dashboard/order/authorize');
        return;
      }
      const result = await orderService.createOrder(payload);
      setSuccess(`Order ${result?.orderNo || ''} created successfully.`);
      resetForm();
    } catch (e) {
      const apiErr =
        e.response?.data?.errors?.items?.[0] ||
        e.response?.data?.message ||
        e.message ||
        'Failed to create order.';
      setError(apiErr);
    } finally {
      setSaving(false);
    }
  };

  const money = (value) => {
    const formatted = num(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return buyerContext.currency ? `${buyerContext.currency} ${formatted}` : formatted;
  };

  return (
    <div className="page-shell">
      <PageHeader
        icon={<FaShoppingCart size={22} />}
        title={isEdit ? 'Edit Order' : 'New Order'}
        subtitle={isEdit
          ? 'Update the order details and items. Saving sends it back for authorization.'
          : 'Select a buyer, pick approved products, and create the order.'}
      />
      <div className="order-page">

      {error && <div className="alert alert-danger py-2 px-3">{error}</div>}
      {success && <div className="alert alert-success py-2 px-3">{success}</div>}

      {/* 1. Buyer Details */}
      <div className="order-card">
        <h5 className="order-section-title">Buyer Details</h5>
        <div className="order-grid">
          <div className="order-field">
            <label htmlFor="ord-client">Buyer Name</label>
            <select id="ord-client" className="form-select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">Select buyer…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.clientId || c.client_id}>
                  {c.name} ({c.clientId || c.client_id})
                </option>
              ))}
            </select>
          </div>
          <div className="order-field">
            <label htmlFor="ord-po">Buyer PO</label>
            <input id="ord-po" type="text" className="form-control" value={buyerPo} onChange={(e) => setBuyerPo(e.target.value)} placeholder="Buyer PO no." />
          </div>
          <div className="order-field">
            <label htmlFor="ord-po-date">Buyer PO Date</label>
            <input id="ord-po-date" type="date" className="form-control" value={buyerPoDate} onChange={(e) => setBuyerPoDate(e.target.value)} />
          </div>
        </div>
      </div>

      {/* 2. Order Information (auto-filled from buyer) */}
      <div className="order-card">
        <h5 className="order-section-title">Order Information</h5>
        <div className="order-grid">
          <div className="order-field">
            <label htmlFor="ord-company">Company</label>
            <select id="ord-company" className="form-select" value={companyBranchId} onChange={(e) => setCompanyBranchId(e.target.value)}>
              <option value="">Select company…</option>
              {branches.map((b) => (
                <option key={b.id ?? b.localKey} value={b.id}>
                  {b.label}{b.companyName ? ` — ${b.companyName}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="order-field">
            <label>Payment Term</label>
            <input className="form-control" value={buyerContext.paymentTerm} readOnly placeholder="—" />
          </div>
          <div className="order-field">
            <label>Port of Delivery</label>
            <input className="form-control" value={buyerContext.portOfDelivery} readOnly placeholder="—" />
          </div>
          <div className="order-field">
            <label>Currency</label>
            <input className="form-control" value={buyerContext.currency} readOnly placeholder="—" />
          </div>
          <div className="order-field">
            <label htmlFor="ord-ship-date">Shipping Date</label>
            <input id="ord-ship-date" type="date" className="form-control" value={shippingDate} onChange={(e) => setShippingDate(e.target.value)} />
          </div>
          <div className="order-field">
            <label htmlFor="ord-del-date">Delivery Date</label>
            <input id="ord-del-date" type="date" className="form-control" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
          </div>
        </div>

        <div className="order-subsection">Shipping Details</div>
        <div className="order-grid order-grid-2">
          <div className="order-field">
            <label>Buyer Address</label>
            <textarea className="form-control" rows={2} value={buyerContext.buyerAddress} readOnly placeholder="—" />
          </div>
          <div className="order-field">
            <label>Consignee</label>
            <textarea className="form-control" rows={2} value={buyerContext.consignee} readOnly placeholder="—" />
          </div>
        </div>

        <div className="order-field mt-2">
          <label htmlFor="ord-comment">Order Comment</label>
          <textarea id="ord-comment" className="form-control" rows={2} value={orderComment} onChange={(e) => setOrderComment(e.target.value)} placeholder="Optional notes for this order" />
        </div>
      </div>

      {/* 3. Product Information */}
      <div className="order-card">
        <div className="order-products-head">
          <h5 className="order-section-title mb-0">Product Information</h5>
          <div className="order-products-actions">
            <input
              type="search"
              className="form-control form-control-sm order-product-search"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder="Search products…"
              disabled={catalogRows.length === 0}
            />
            <button type="button" className="btn btn-primary btn-sm" disabled={selectedUids.size === 0} onClick={addSelectedToList}>
              <FaPlus className="me-1" /> Add to List
            </button>
          </div>
        </div>

        {!clientId ? (
          <div className="order-empty">Select a buyer to load their approved products.</div>
        ) : loadingItems ? (
          <div className="order-empty">Loading approved products…</div>
        ) : catalogRows.length === 0 ? (
          <div className="order-empty">This buyer has no approved products.</div>
        ) : (
          <div className="table-responsive">
            <table className="order-table">
              <thead>
                <tr>
                  <th className="order-check-col"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} /></th>
                  <th>P.Code</th>
                  <th>Image</th>
                  <th>Description</th>
                  <th>Client SKU</th>
                  <th>Barcode/EAN</th>
                  <th>Inner Qty</th>
                  <th>Master Qty</th>
                  <th>CBM/Carton</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {filteredCatalogRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="order-no-match">No products match “{productSearch}”.</td>
                  </tr>
                )}
                {filteredCatalogRows.map((row) => (
                  <tr key={row.productUid} className={selectedUids.has(row.productUid) ? 'is-selected' : ''}>
                    <td className="order-check-col">
                      <input type="checkbox" checked={selectedUids.has(row.productUid)} onChange={() => toggleSelect(row.productUid)} />
                    </td>
                    <td>{row.pCode}</td>
                    <td>{row.image ? <img src={row.image} alt={row.pCode} className="order-thumb" /> : <span className="text-muted">—</span>}</td>
                    <td className="order-desc-cell">
                      <div className="order-desc-name">{row.description}</div>
                      {row.color && <div className="order-desc-sub">Color: {row.color}</div>}
                      {row.dimension && <div className="order-desc-sub">{row.dimension}</div>}
                    </td>
                    <td>{row.clientSku}</td>
                    <td>{row.barcode || '—'}</td>
                    <td>{row.innerQty || '—'}</td>
                    <td>{row.masterQty || '—'}</td>
                    <td>{num(row.cbmPerCarton).toFixed(4)}</td>
                    <td>{money(row.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Selected products list */}
        <div className="order-subsection">Selected Products {orderItems.length > 0 && `(${orderItems.length})`}</div>
        {orderItems.length === 0 ? (
          <div className="order-empty">No products added yet. Check items above and click “Add to List”.</div>
        ) : (
          <div className="table-responsive">
            <table className="order-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>P.Code</th>
                  <th>Description</th>
                  <th>Client SKU</th>
                  <th>Master Qty</th>
                  <th>No. Cartons</th>
                  <th>Order Qty</th>
                  <th>CBM/Carton</th>
                  <th>Total CBM</th>
                  <th>Price</th>
                  <th>Net Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {computedItems.map((row, index) => (
                  <tr key={row.productUid}>
                    <td>{index + 1}</td>
                    <td>{row.pCode}</td>
                    <td className="order-desc-cell">
                      <div className="order-desc-name">{row.description}</div>
                      {row.color && <div className="order-desc-sub">Color: {row.color}</div>}
                    </td>
                    <td>{row.clientSku}</td>
                    <td>{row.masterQty || '—'}</td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="order-qty-input"
                        value={row.cartons}
                        onChange={(e) => handleCartonsChange(row.productUid, e.target.value)}
                      />
                    </td>
                    <td>{row.orderQty.toLocaleString()}</td>
                    <td>{num(row.cbmPerCarton).toFixed(4)}</td>
                    <td>{row.totalCbm.toFixed(4)}</td>
                    <td>{money(row.price)}</td>
                    <td>{money(row.netAmount)}</td>
                    <td>
                      <button type="button" className="btn btn-link text-danger p-0" title="Remove" onClick={() => removeFromList(row.productUid)}>
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={10} className="text-end fw-bold">Tot Amount</td>
                  <td className="fw-bold">{money(totalAmount)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* 4. Order Summary */}
      <div className="order-card">
        <h5 className="order-section-title">Order Summary</h5>
        <div className="order-grid">
          <div className="order-field">
            <label htmlFor="ord-rec">Rec. Date</label>
            <input id="ord-rec" type="date" className="form-control" value={recDate} onChange={(e) => setRecDate(e.target.value)} />
          </div>
          <div className="order-field">
            <label htmlFor="ord-exf">Ex Factory Date</label>
            <input id="ord-exf" type="date" className="form-control" value={exFactoryDate} onChange={(e) => setExFactoryDate(e.target.value)} />
          </div>
          <div className="order-field">
            <label htmlFor="ord-cancel">Cancel Date</label>
            <input id="ord-cancel" type="date" className="form-control" value={cancelDate} onChange={(e) => setCancelDate(e.target.value)} />
          </div>
          <div className="order-field">
            <label htmlFor="ord-terms">Shipment Terms</label>
            <select id="ord-terms" className="form-select" value={shipmentTerms} onChange={(e) => setShipmentTerms(e.target.value)}>
              {SHIPMENT_TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="order-field">
            <label htmlFor="ord-mode">Mode of Shipment</label>
            <select id="ord-mode" className="form-select" value={modeOfShipment} onChange={(e) => setModeOfShipment(e.target.value)}>
              {MODE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="order-field">
            <label htmlFor="ord-adv">Advance Received</label>
            <input id="ord-adv" type="number" min="0" className="form-control" value={advanceReceived} onChange={(e) => setAdvanceReceived(e.target.value)} />
          </div>
          <div className="order-field">
            <label htmlFor="ord-disc">Discount Amount</label>
            <input id="ord-disc" type="number" min="0" className="form-control" value={discountAmount} onChange={(e) => setDiscountAmount(e.target.value)} />
          </div>
          <div className="order-field">
            <label htmlFor="ord-add">Add Amount</label>
            <input id="ord-add" type="number" min="0" className="form-control" value={addAmount} onChange={(e) => setAddAmount(e.target.value)} />
          </div>
          <div className="order-field">
            <label>Grand Total</label>
            <input className="form-control order-grand-total" value={money(grandTotal)} readOnly />
          </div>
        </div>

        <div className="order-field mt-2">
          <label htmlFor="ord-special">Special Notes</label>
          <textarea id="ord-special" className="form-control" rows={2} value={specialNotes} onChange={(e) => setSpecialNotes(e.target.value)} placeholder="Optional" />
        </div>

        <div className="order-save-bar">
          <span className="text-muted">
            {computedItems.length} item(s) · Tot Amount: {money(totalAmount)} · Grand Total: {money(grandTotal)}
          </span>
          <button type="button" className="btn btn-success" disabled={!canSave} onClick={handleSave}>
            {saving ? 'Saving…' : isEdit ? 'Update Order' : 'Save Order'}
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default OrderCreation;
